class AlgoliaFilterButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));

    // Start once the search grid's InstantSearch runs (the theme's algoliaBridge, window.gibsonAlgolia)
    this.handleAlgoliaStart = () => {
      if (this.start()) {
        window.removeEventListener('gibson:algolia:init', this.handleAlgoliaStart);
        window.removeEventListener('gibson:algolia:render', this.handleAlgoliaStart);
      }
    };
    if (!this.start()) {
      window.addEventListener('gibson:algolia:init', this.handleAlgoliaStart);
      window.addEventListener('gibson:algolia:render', this.handleAlgoliaStart);
    }
  }

  /**
   * Reads the active filters and starts following state changes, once the helper exists.
   * @returns {boolean} Whether it started (now or earlier)
   */
  start() {
    if (this.started) return true;
    const helper = this.getHelper();
    if (!helper) return false;

    this.initializeState();
    AlgoliaFilterButton.listenTo(helper);
    this.started = true;
    return true;
  }

  getHelper() {
    return window.gibsonAlgolia ? window.gibsonAlgolia.getHelper() : null;
  }

  /**
   * Follows the helper's state for all filter buttons with one listener. Each button used to add its own, and each
   * of those updated every button, so a change ran once per button; more than 10 buttons also tripped the helper's
   * EventEmitter "memory leak" warning.
   */
  static listenTo(helper) {
    if (AlgoliaFilterButton.helper === helper) return;
    AlgoliaFilterButton.stopListening();
    AlgoliaFilterButton.helper = helper;
    helper.on('change', AlgoliaFilterButton.updateButtons);
  }

  static stopListening() {
    if (!AlgoliaFilterButton.helper) return;
    AlgoliaFilterButton.helper.removeListener('change', AlgoliaFilterButton.updateButtons);
    AlgoliaFilterButton.helper = null;
  }

  static updateButtons({ state }) {
    // Get all active filters
    const activeFilters = {
      disjunctiveFacets: state.disjunctiveFacetsRefinements || {},
      otherRefinements: state.facetsRefinements || {},
    };

    // Get all filter buttons
    const filterButtons = document.querySelectorAll('algolia-filter-button');

    // Reset all buttons first
    filterButtons.forEach((button) => {
      button.classList.remove('is-active');
    });

    // Check each button against active filters
    filterButtons.forEach((button) => {
      const buttonValue = button.dataset.value;

      // Parse the URL-style parameter into Algolia format (same as initializeState)
      const params = new URLSearchParams(buttonValue);
      const refinements = {};

      params.forEach((value, key) => {
        const match = key.match(/refinementList\[(.*?)\]/);
        if (match) {
          const attribute = match[1];
          if (!refinements[attribute]) {
            refinements[attribute] = [];
          }
          refinements[attribute].push(value);
        }
      });

      // Check if any of this button's refinements are active (same logic as initializeState)
      const isActive = Object.entries(refinements).some(([attribute, values]) => {
        return values.some((value) => {
          const hasExactMatch =
            activeFilters.disjunctiveFacets[attribute] && activeFilters.disjunctiveFacets[attribute].includes(value);

          // Check if removing the last word matches any existing refinements
          const words = value.split(' ');
          const partialMatch = words.length > 1 ? words.slice(0, -1).join(' ') : null;
          const hasPartialMatch =
            partialMatch &&
            activeFilters.disjunctiveFacets[attribute] &&
            activeFilters.disjunctiveFacets[attribute].includes(partialMatch);

          return hasExactMatch || hasPartialMatch;
        });
      });

      if (isActive) {
        button.classList.add('is-active');
      }
    });
  }

  disconnectedCallback() {
    // Clean up the listeners when the element is removed
    window.removeEventListener('gibson:algolia:init', this.handleAlgoliaStart);
    window.removeEventListener('gibson:algolia:render', this.handleAlgoliaStart);

    // The shared helper listener goes with the last button
    if (this.started && !document.querySelector('algolia-filter-button')) AlgoliaFilterButton.stopListening();
    this.started = false;
  }

  initializeState() {
    const helper = this.getHelper();

    const { value } = this.dataset;

    // Parse the URL-style parameter into Algolia format
    const params = new URLSearchParams(value);
    const refinements = {};

    params.forEach((value, key) => {
      const match = key.match(/refinementList\[(.*?)\]/);
      if (match) {
        const attribute = match[1];
        refinements[attribute] = [value];
      }
    });

    // Check if any of our refinements are active
    Object.entries(refinements).forEach(([attribute, values]) => {
      const isActive = values.some((value) => {
        const hasExactMatch =
          helper.state.disjunctiveFacetsRefinements &&
          helper.state.disjunctiveFacetsRefinements[attribute] &&
          helper.state.disjunctiveFacetsRefinements[attribute].includes(value);

        // Check if removing the last word matches any existing refinements
        const words = value.split(' ');
        const partialMatch = words.length > 1 ? words.slice(0, -1).join(' ') : null;
        const hasPartialMatch =
          partialMatch &&
          helper.state.disjunctiveFacetsRefinements &&
          helper.state.disjunctiveFacetsRefinements[attribute] &&
          helper.state.disjunctiveFacetsRefinements[attribute].includes(partialMatch);

        return hasExactMatch || hasPartialMatch;
      });

      if (isActive) {
        this.classList.add('is-active');
      }
    });
  }

  clearActiveFilters() {
    let allActiveFilters = document.querySelectorAll('.algolia-filter-button.is-active');
    allActiveFilters.forEach((filter) => {
      filter.classList.remove('is-active');
    });
  }

  handleClick() {
    const helper = this.getHelper();
    if (!helper) return;
    this.clearActiveFilters();

    const { value } = this.dataset;

    try {
      // Parse the URL-style parameter into Algolia format
      const params = new URLSearchParams(value);
      const refinements = {};

      // Collect all refinements, handling array-style parameters
      params.forEach((value, key) => {
        // Match both refinementList[attribute] and refinementList[attribute][0]
        const match = key.match(/refinementList\[(.*?)\](?:\[\d+\])?$/);
        if (match) {
          const attribute = match[1];
          if (!refinements[attribute]) {
            refinements[attribute] = [];
          }
          refinements[attribute].push(value);
        }
      });

      // Check if any of the refinements are active
      const hasActiveRefinement = Object.entries(refinements).some(([attribute, values]) =>
        values.some(
          (value) =>
            helper.state.disjunctiveFacetsRefinements &&
            helper.state.disjunctiveFacetsRefinements[attribute] &&
            helper.state.disjunctiveFacetsRefinements[attribute].includes(value),
        ),
      );

      if (hasActiveRefinement) {
        // When turning off, remove all refinements from this button
        Object.entries(refinements).forEach(([attribute, values]) => {
          values.forEach((value) => helper.removeDisjunctiveFacetRefinement(attribute, value));
        });
        this.classList.remove('is-active');
        // Remove params from URL
        const newUrl = new URL(window.location.href);
        params.forEach((_, key) => {
          if (key.startsWith('refinementList')) {
            newUrl.searchParams.delete(key);
          }
        });
        window.history.pushState({}, '', newUrl);
      } else {
        // When turning on, clear all existing refinements first. In Stock too: it was a disjunctive facet until the
        // switch became the app's conjunctive one
        helper.state.disjunctiveFacets.forEach((facet) => {
          helper.clearRefinements(facet);
        });
        if (helper.state.isConjunctiveFacet('inventory_available')) helper.clearRefinements('inventory_available');

        // Then add all our new refinements, always as disjunctive ones: the active state above and the toggle-off
        // only read disjunctive refinements. Some attributes (e.g. the Exclusive flags) are conjunctive in the
        // app, so declare them disjunctive too, as this button always has.
        Object.entries(refinements).forEach(([attribute, values]) => {
          if (!helper.state.isDisjunctiveFacet(attribute)) {
            helper.setState(helper.state.addDisjunctiveFacet(attribute));
          }
          values.forEach((value) => helper.addDisjunctiveFacetRefinement(attribute, value));
        });

        // Replace URL params with our new ones
        const newUrl = new URL(window.location.href);
        newUrl.search = value;
        window.history.pushState({}, '', newUrl);
      }

      // Open filters if they're not already open
      const filterButton = document.querySelector('.ais-toggle-filters');
      if (filterButton && !document.querySelector('.ais-facets').classList.contains('ais-facets__shown')) {
        filterButton.click();
      }

      // Trigger a new search
      helper.search();
    } catch (error) {
      console.error('Error during refinement toggle:', error);
    }
  }
}

customElements.define('algolia-filter-button', AlgoliaFilterButton);
//
